/*
 * This file is categorized as "Custom Source Code"
 * and is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 */
import { useState, useEffect, } from 'react';
import {
	Alert,
} from 'react-native';
import {
	Button,
	Column,
	Icon,
	Image,
	Pressable,
	Text,
} from 'native-base';
import {
	FontAwesome5,
} from '@expo/vector-icons';
import {
	USER,
	EMAIL,
} from '../constants/Vars';
import { useKeepAwake } from 'expo-keep-awake';
import oneHatData from '@onehat/data';
import getIsMobile from '../OneHat/functions/getIsMobile';
import AppGlobals from '../AppGlobals';
import withRedux from '../OneHat/data/hoc/withRedux';
import {
	confirmCacheSystemWorks,
	createCacheSpace,
	deleteCachedFiles,
	downloadAndCacheFile,
	initCacheSystem,
} from '../functions/CacheSystem';
import ProgressModal from '../OneHat/components/ProgressModal';
import delay from '../OneHat/functions/delay';
import { logoutBecauseExpiredAuthentication } from '../models/Thunks/setQuestion';
import getTokenHeaders from '../OneHat/functions/getTokenHeaders';
import ScreenContainer from '../OneHat/components/ScreenContainer';
import testProps from '../OneHat/functions/testProps';
import wsc3logos from '../img/wsc3logos.png';
import _ from 'lodash';


function Home(props) {
	const {
			relogin,
			actions,
			navigation,
		} = props,
		isMobile = getIsMobile(),
		unused = useKeepAwake(),
		[initiallyClicked, setInitiallyClicked] = useState(false),
		Secure = oneHatData.getRepository('Secure'),
		Proofs = oneHatData.getRepository('Proofs'),
		Prefs = oneHatData.getRepository('Prefs'),
		Divisions = oneHatData.getRepository('Divisions'),
		Questions = oneHatData.getRepository('Questions'),
		Songs = oneHatData.getRepository('Songs'),
		Events = oneHatData.getRepository('Events'),
		initializeAudioSystem = () => {
			actions.setProgressMessage('Set up audio player');
			actions.setProgressPercentage(0);
			if (!AppGlobals.audioPlayer) {
				actions.setUpAudio();
			}
			actions.setProgressPercentage(99);
		},
		setFirstTenSongs = async () => {

			// Download songs for the first ten questions if necessary
			if (isMobile) {
				actions.setProgressMessage('Set audio files');
				let i, songs, n, song, fileUri, remoteUri;
				const format = 'mp3',
					questionsCount = 10,
					songProgressPercent = (90 - 35) / questionsCount; // Progress will go from 35 to 90 in this segment
				for (i = 1; i <= questionsCount; i++) {
					actions.setProgressPercentage(35 + (i * 5));
					songs = Songs.getBy((song) => {
						return song.questions__id === i;
					});
					for (n = 0; n < songs.length; n++) {
						song = songs[n];
						fileUri = song.songs__file + '.' + format;
						remoteUri = AppGlobals.mediaBaseURL + song.songs__file + '.' + format;
						try {
							await downloadAndCacheFile(remoteUri, fileUri, ({ totalBytesWritten, totalBytesExpectedToWrite }) => {
								const downloadedPercent = totalBytesWritten / totalBytesExpectedToWrite,
									startingPercent = songProgressPercent * n,
									addedPercent = songProgressPercent * downloadedPercent,
									currentPercent = startingPercent + addedPercent;
									actions.setProgressPercentage(currentPercent);
							});
						} catch(e) {
							if (e.status === 401) {
								logoutBecauseExpiredAuthentication();
								return;
							} else {
								throw new Error('HTTP error in file download.');
							}
						}
					}
				}
			}
		},
		setPrefs = async () => {
			// Set prefs to Redux
			actions.setProgressMessage('Restoring prefs');
			await delay(10);
			let i, pref;
			for (i = 0; i < Prefs.entities.length; i++) {
				pref = Prefs.entities[i];
				switch(pref.key) {
					case 'audioIsKaraokeMode':
						actions.setIsKaraoke(pref.value === 'true');
						break;
					case 'audioIsLooping':
						actions.setIsLooping(pref.value === 'true');
						break;
					case 'audioIsAutoAdvance':
						actions.setIsAutoAdvance(pref.value === 'true');
						break;
					case 'audioIsLyricsMode':
						actions.setAnimatedLyrics(pref.value === 'true');
						break;
					case 'showQuestionsInDivisions':
						actions.setShowQuestionsInDivisions(pref.value === 'true');
						break;
					case EMAIL:
						actions.setEmail(pref.value);
						break;
					case 'question_id':
						actions.setQuestionById(parseInt(pref.value));
						break;
				}
			}
			actions.setProgressPercentage(100);
			await delay(10);
		};

	useEffect(() => {
		
		if (Divisions.isDestroyed || Questions.isDestroyed || Prefs.isDestroyed || Proofs.isDestroyed || Events.isDestroyed || Secure.isDestroyed || Songs.isDestroyed) {
			return; // for auto-reload of Expo
		}

		(async () => {

			try {

				if (isMobile) {
					initializeAudioSystem();

					actions.setProgressMessage('Restore user');
					actions.setProgressPercentage(0);
					const jsonStr = await Secure.getById(USER);
					let user;
					if (jsonStr) {
						user = JSON.parse(jsonStr.value);
					}
					let headers = {};
					if (user) {
						actions.setUser(user);
						headers = getTokenHeaders();
		
						// Apply auth token to all Repositories
						oneHatData.setOptionsOnAllRepositories({ headers }); // already created
						oneHatData.setRepositoryGlobals({ headers }); // not yet created
					}
					actions.setProgressPercentage(5);
				}

				// Get data from server if necessary
				actions.setProgressMessage('Download divisions');
				let lastSync;
				lastSync = await Divisions.getLastSync();
				if (lastSync && lastSync.isBefore(AppGlobals.clearDataBefore)) {
					Divisions.deleteAll();
				}
				if (!Divisions.entities.length) {
					await Divisions.sync();
					Divisions.decodeHtmlEntities();
				}
				actions.setProgressPercentage(10);
				
				actions.setProgressMessage('Download questions');
				lastSync = await Questions.getLastSync();
				if (lastSync && lastSync.isBefore(AppGlobals.clearDataBefore)) {
					Questions.deleteAll();
				}
				if (!Questions.entities.length) {
					await Questions.sync();
					Questions.decodeHtmlEntities();
				}
				actions.setProgressPercentage(15);
				
				actions.setProgressMessage('Download proofs');
				lastSync = await Proofs.getLastSync();
				if (lastSync && lastSync.isBefore(AppGlobals.clearDataBefore)) {
					Proofs.deleteAll();
				}
				if (!Proofs.entities.length) {
					await Proofs.sync();
				}
				actions.setProgressPercentage(20);
				
				actions.setProgressMessage('Download events');
				lastSync = await Events.getLastSync();
				if (lastSync && lastSync.isBefore(AppGlobals.clearDataBefore)) {
					Events.deleteAll();
				}
				if (!Events.entities.length) {
					await Events.sync();
					Events.decodeHtmlEntities();
				}
				actions.setProgressPercentage(25);
				
				actions.setProgressMessage('Download songs');
				lastSync = await Songs.getLastSync();
				if (lastSync && lastSync.isBefore(AppGlobals.clearDataBefore)) {
					Songs.deleteAll();
				}
				if (!Songs.entities.length) {
					await Songs.sync();
				}
				actions.setProgressPercentage(30);


				// Deal with cached files
				actions.setProgressMessage('Download audio files');
				const works = confirmCacheSystemWorks();
				if (!works) {
					throw Error('Cache system cannot be used on this device.');
					// TODO: Show error on screen
				}
				await initCacheSystem();
				if (AppGlobals.clearAllFiles) {
					await deleteCachedFiles();
				}
				await createCacheSpace();
				actions.setProgressPercentage(35);

				if (isMobile) {
					await setFirstTenSongs();
					await setPrefs();
				}
				actions.setProgressPercentage(100);

			} catch (e) {
				actions.setProgressPercentage(100);
				Alert.alert('Error', e);
			}

		})();

		return () => {
			actions.tearDownAudio();
		};
	}, []);

	useEffect(() => {
		if (!relogin) {
			return;
		}
		
		navigation.navigate('Account');
	}, [relogin]);

	useEffect(async () => {
		if (!initiallyClicked || isMobile) {
			return;
		}
		
		initializeAudioSystem();
		await setFirstTenSongs();
		await setPrefs();

	}, [initiallyClicked]);

	if (!isMobile && !initiallyClicked) {
		// On web, browser restrictions mean we need a click before we can initialize the audio system
		return <ScreenContainer screenName="Home" p={10} bg="primary.500" safeArea={true}>
					<Column
						flex={1}
						w="60%"
						justifyContent="center"
					>
						<ProgressModal />
						<Button onPress={() => { setInitiallyClicked(true); }} bg="primary.800">Start App</Button>
					</Column>
				</ScreenContainer>;
	}

	const text1 = {
			fontFamily: 'RobotoCondensed_400Regular',
			fontSize: 25,
			lineHeight: 27,
			textAlign: 'center',
			color: '#fff',
		},
		text2 = {
			fontFamily: 'Roboto_700Bold',
			textAlign: 'center',
			fontSize: 30,
			lineHeight: 32,
			color: '#fff',
		};
		
	return <ScreenContainer screenName="Home" p={10} bg="primary.500" safeArea={true}>
				<ProgressModal />
				
				<Column flex={5} w="100%" pb={10}>
					<Column flex={1} alignItems="center" justifyContent="center">
						<Text {...text1}>SONGS FOR THE</Text>
						<Text {...text1}>WESTMINSTER SHORTER CATECHISM</Text>
						<Text {...text1}>IN MODERN ENGLISH</Text>
					</Column>
					<Pressable w="100%" flex={3} py={4} justifyContent="center" onPress={() => navigation.navigate('Questions')}>
						<Image
							{...testProps('appImg')}
							source={wsc3logos}
							w="100%"
							flex={1}
							resizeMode="contain"
							alt="App Logo"
						/>
					</Pressable>
					<Column flex={1} alignItems="center" justifyContent="center">
						<Text {...text2}>Great Theology</Text>
						<Text {...text2}>& Memorable Music</Text>
					</Column>
				</Column>

				<Column
					w="100%"
					maxWidth="300px"
				>
					<Button
						{...testProps('catechismBtn')}
						leftIcon={<Icon as={FontAwesome5} name="book" size="sm" />}
						onPress={() => navigation.navigate('Questions')}
						bg="primary.800"
						mb={3}
					>The Catechism</Button>

					<Button
						{...testProps('settingsBtn')}
						leftIcon={<Icon as={FontAwesome5} name="cog" size="sm" />}
						onPress={() => navigation.navigate('Settings')}
						bg="primary.700"
						mb={3}
					>Settings</Button>

					{isMobile ? <Button
									{...testProps('accountBtn')}
									leftIcon={<Icon as={FontAwesome5} name="user-alt" />}
									onPress={() => navigation.navigate('Account')}
									bg="primary.700"
									mb={3}
								>Account</Button> : null}

					<Button
						{...testProps('infoBtn')}
						leftIcon={<Icon as={FontAwesome5} name="info-circle" size="sm" />}
						onPress={() => navigation.navigate('Info')}
						bg="primary.700"
						mb={3}
					>Info</Button>

				</Column>
			</ScreenContainer>;
}

export default withRedux(Home, [
	'relogin',
]);